/**
 * Languages the product speaks, and the translation of sentences the server
 * produces.
 *
 * The interface's own text lives with the web app (`apps/web/src/i18n`). This
 * file covers what it cannot: sentences written by the API and the worker —
 * error messages, status explanations, failure reasons. Many of those are
 * stored in the database (`failure_reason`, `chapters_error`, …) and read by
 * every member of a workspace, whatever language each of them uses, so they
 * cannot be written in the reader's language at the time they are stored.
 *
 * The English sentence is therefore the stable key, and it is translated at
 * the moment it is shown. `server-text.test.ts` scans the server code and
 * fails when a user-facing sentence has no entry here, so a new message
 * cannot ship English-only by accident. When you change a sentence on the
 * server, change its entry here in the same commit.
 */

export const LOCALES = ["en", "fr"] as const;
export type Locale = (typeof LOCALES)[number];
export const DEFAULT_LOCALE: Locale = "en";

export function isLocale(value: unknown): value is Locale {
  return typeof value === "string" && (LOCALES as readonly string[]).includes(value);
}

/** Each language named in itself — what a language picker should show. */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: "English",
  fr: "Français",
};

/**
 * One server sentence and its translation.
 *
 * `en` may contain `{name}` placeholders standing for any text the server
 * interpolated (a platform name, a size, a filename). The same names in `fr`
 * receive the captured values. Placeholders match lazily, so put the most
 * specific entries first when two could match the same sentence.
 */
export type ServerTextEntry = { en: string; fr: string };

type Compiled = { pattern: RegExp; names: string[]; fr: string };

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

/**
 * Build the lookup for a catalogue: exact sentences in a map, sentences with
 * placeholders as anchored patterns tried in order.
 */
export function compileServerText(entries: readonly ServerTextEntry[]) {
  const exact = new Map<string, string>();
  const patterns: Compiled[] = [];

  for (const entry of entries) {
    const names = [...entry.en.matchAll(PLACEHOLDER)].map((m) => m[1]!);
    if (names.length === 0) {
      exact.set(entry.en, entry.fr);
      continue;
    }
    const source = entry.en
      .split(PLACEHOLDER)
      .map((part, i) => (i % 2 === 1 ? "([\\s\\S]+?)" : escapeRegExp(part)))
      .join("");
    patterns.push({ pattern: new RegExp(`^${source}$`), names, fr: entry.fr });
  }

  return function translate(text: string, locale: Locale): string {
    if (locale === "en" || !text) return text;

    const direct = exact.get(text);
    if (direct !== undefined) return direct;

    // Server errors are sometimes several sentences joined, e.g. an upload
    // error with "Choose the same file again to resume." appended by the
    // client. Translate each known sentence and keep the rest as it was.
    for (const { pattern, names, fr } of patterns) {
      const match = pattern.exec(text);
      if (!match) continue;
      const values = Object.fromEntries(names.map((name, i) => [name, match[i + 1] ?? ""]));
      return fr.replace(PLACEHOLDER, (_, name: string) => {
        const value = values[name] ?? "";
        // A captured value can itself be a known sentence ("{reason} Try
        // again."), so give it one more pass.
        return exact.get(value) ?? value;
      });
    }

    return text;
  };
}
